import type { ReactElement } from 'react';
import type { BacktestingConfiguration } from '../BacktestConfiguration.types.ts';
import { Stack, Box, Typography } from '@mui/joy';
import { usePortfolioRiskSummarySuspenseQuery } from '../../../../../generated/graphql.tsx';
import { convertDateInUtcToUTCISODate } from '../../../../date.utils.ts';
import { parseUtcDate } from '../../../../date.utils.ts';
import {
  PORTFOLIO_VOLATILITY_METRIC,
  PORTFOLIO_SHARPE_METRIC,
  PORTFOLIO_SORTINO_METRIC,
  PORTFOLIO_TRACKING_ERROR_METRIC,
  realized,
  expanding,
  annualized,
  benchmarkParameter,
} from '../../../../metrics/PortfolioMetricsData.ts';
import GAgGrid from '../../../../technical/grids/GAgGrid.tsx';
import { gridWithInputStyles } from '../../../../technical/grids/gridStyles.ts';
import type { ColDef } from 'ag-grid-community';
import type { IMetricCalculatorInput, IMetricCalculatorParameterInput } from '../../../../../generated/graphql.tsx';
import { formatNumber } from 'components/formatter.utils.ts';
import { formatPercentage } from '../../../../formatter.utils';
import { calculateCummulativeReturns } from '../../returnsMath.ts';
import { bignumber } from 'mathjs';
import { metricsData } from 'components/metrics/PortfolioMetricsData.ts';
import type { MetricParams } from 'components/metrics/MetricsData.types.ts';
import { isNil } from 'lodash/fp';
import type { NotVerifiedAssetWithId } from 'components/market/asset/AssetService.tsx';
// Not yet defined in the portfolio metrics data
// const INFORMATION_RATIO_METRIC = 'pmet:information_ratio';

const metrics = (benchmark: {
  id: string;
  symbol: string;
}): Array<{
  label: string;
  parameters: MetricParams & { assetId?: string };
}> => [
  {
    label: PORTFOLIO_VOLATILITY_METRIC,
    parameters: {
      metricType: 'realized',
      windowType: 'expanding',
      annualized: true,
    },
  },
  {
    label: PORTFOLIO_SHARPE_METRIC,
    parameters: {
      metricType: 'realized',
      windowType: 'expanding',
      annualized: true,
    },
  },
  {
    label: PORTFOLIO_SORTINO_METRIC,
    parameters: {
      metricType: 'realized',
      windowType: 'expanding',
      annualized: true,
    },
  },
  {
    label: PORTFOLIO_TRACKING_ERROR_METRIC,
    parameters: {
      metricType: 'realized',
      windowType: 'expanding',
      annualized: true,
      assetSymbol: benchmark.symbol,
      assetId: benchmark.id,
    },
  },
  // {
  //   label: INFORMATION_RATIO_METRIC,
  //   parameters: [realized, expanding, benchmarkParameter(btcId)],
  // },
];

const convertMetricToRequest = (
  metrics: {
    label: string;
    parameters: MetricParams & { assetId?: string };
  }[]
): IMetricCalculatorInput[] => {
  return metrics.map((metric) => {
    const parameters: IMetricCalculatorParameterInput[] = [];
    if (metric.parameters.assetId) {
      parameters.push(benchmarkParameter(metric.parameters.assetId));
    }

    if (metric.parameters.annualized) {
      parameters.push(annualized);
    }

    if (metric.parameters.windowType === 'expanding') {
      parameters.push(expanding);
    }

    if (metric.parameters.metricType === 'realized') {
      parameters.push(realized);
    }

    return {
      label: metric.label,
      parameters: parameters,
    };
  });
};

const RiskSummaryContainer = (props: {
  btcBenchmark: NotVerifiedAssetWithId & { symbol: string };
  backtestingConfig: BacktestingConfiguration;
}): ReactElement => {
  const { btcBenchmark, backtestingConfig } = props;

  const finalMetrics = metrics(btcBenchmark);
  interface RowData extends Record<(typeof finalMetrics)[number]['label'], string | number> {
    portfolio: string;
    returns: number;
  }

  const query = usePortfolioRiskSummarySuspenseQuery({
    variables: {
      input: {
        metrics: convertMetricToRequest(finalMetrics),
        portfolioDefinitionIds: backtestingConfig.portfolios.map((port) => port.id),
        since: backtestingConfig.range.since,
        to: backtestingConfig.range.to,
      },
      portfolioIds: backtestingConfig.portfolios.map((port) => port.id),
      returnSince: backtestingConfig.range.since
        ? convertDateInUtcToUTCISODate(parseUtcDate(backtestingConfig.range.since).subtract(1, 'day'))
        : null,
      returnTo: backtestingConfig.range.to,
    },
  });

  const analysis = query.data.portfolio.analysis;
  const filteredAnalysis = analysis.map((portfolioAnalysis) => {
    const returns = portfolioAnalysis.returns;
    if (returns.length === 0) {
      return portfolioAnalysis;
    }

    const firstDay = portfolioAnalysis.returns[0].date;
    const filteredReturns = portfolioAnalysis.returns.filter((r) => r.date !== firstDay);
    return {
      ...portfolioAnalysis,
      returns: filteredReturns,
    };
  });

  // Process the data for the ag-grid
  const processDataForSummary = () => {
    if (!query.data) return { rowData: [], columnDefs: [] };

    // Create a map of portfolio IDs to their names and analysis data
    const portfolioMap = new Map<string, { name: string; analysis?: (typeof filteredAnalysis)[number] }>();

    // First, map all portfolios with their names
    for (const portfolio of backtestingConfig.portfolios) {
      portfolioMap.set(portfolio.id, { name: portfolio.name });
    }

    // Then, match each analysis with its corresponding portfolio
    for (const analysis of filteredAnalysis) {
      const portfolioId = analysis.portfolioDefinition.id;
      const portfolioData = portfolioMap.get(portfolioId);
      if (portfolioData) {
        portfolioData.analysis = analysis;
      }
    }

    const columnDefs: ColDef<Partial<RowData>>[] = [
      {
        field: 'portfolio',
        headerName: 'Portfolio',
        width: 200,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center',
      },
      {
        field: 'returns',
        headerName: 'Annualized returns',
        width: 200,
        type: ['numericColumn', 'percentageColumn'],
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center',
      },
    ];

    for (const metric of finalMetrics) {
      const metricInfo = metricsData[metric.label as keyof typeof metricsData];
      const name = typeof metricInfo.name === 'function' ? metricInfo.name(metric.parameters) : metricInfo.name;
      columnDefs.push({
        field: metric.label,
        headerName: name,
        type: metricInfo.format === 'percentage' ? ['numericColumn', 'percentageColumn'] : ['numericColumn'],
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center',
        width: 200,
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return '';
          if (metricInfo.format === 'percentage') {
            return formatPercentage(params.value);
          }
          return formatNumber(params.value);
        },
      });
    }

    // Create row data for the summary table
    const rowData: Array<Partial<RowData>> = [];

    // Add a row for each portfolio
    for (const [id, { name, analysis }] of portfolioMap.entries()) {
      const row: Partial<RowData> = { portfolio: name };

      // Add annualized returns
      if (analysis?.returns && analysis.returns.length > 0) {
        const returns = analysis.returns;
        const days = returns.length;
        const result = calculateCummulativeReturns(returns.map((val) => bignumber(val.portfolioWeightedReturn))).at(-1);
        if (!result) {
          console.error('No result found for portfolio', name);
          continue;
        }
        const annualizedReturn = (1 + result.toNumber()) ** (365 / days) - 1;
        row.returns = annualizedReturn;
      }

      for (const metric of query.data.portfolio.metrics) {
        if (isNil(metric.portfolioDefinition) || metric.portfolioDefinition.id !== id) {
          continue;
        }

        const val = metric.series.at(0)?.values.at(-1);
        if (isNil(val)) {
          continue;
        }

        row[metric.label] = Number.parseFloat(val.value);
      }

      rowData.push(row);
    }

    return { rowData, columnDefs };
  };

  const { rowData: summaryRowData, columnDefs: summaryColumnDefs } = processDataForSummary();

  return (
    <Stack gap={2}>
      <Typography level="h4">Summary</Typography>
      <Box height="400px">
        <GAgGrid<Partial<RowData>>
          rowData={summaryRowData}
          columnDefs={summaryColumnDefs}
          autoSizeStrategy={{ type: 'fitCellContents' }}
          enableCharts
          cellSelection
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: false,
          }}
          rowHeight={gridWithInputStyles.rowHeight}
        />
      </Box>
    </Stack>
  );
};

export default RiskSummaryContainer;
